/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { InsightsDataService } from '../../insights-data.service';
import { CommonModule } from '@angular/common';
import { frequencyIntervalGraph } from '../../insights-data.interface';

@Component({
  selector: 'app-frequency-interval-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './frequency-interval-graph.component.html',
  styleUrl: './frequency-interval-graph.component.scss',
})
export class FrequencyIntervalGraphComponent {
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = [];
  renderedChartsCount = 0;
  allChartsRendered = false;
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 15;
  groupSize = 5;
  behaviorId = 0;
  @Input() set graphData(freqDatasheetData: frequencyIntervalGraph) {
    this.behaviorId = freqDatasheetData.caseBehaviorId ?? 0;
    this.setGraphOptionsDefault(freqDatasheetData, false);
    this.setGraphOptionsDefault(freqDatasheetData, true);
  }

  constructor(
    private elementRef: ElementRef,
    private insightService: InsightsDataService
  ) {}

  setGraphOptionsDefault(
    freqDatasheetData: frequencyIntervalGraph,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      const { xLabel, frequencyData } = freqDatasheetData;
      this.totalCharts = 1;
      const hasValidData = xLabel.some(date =>
        Object.values(frequencyData).some(
          timeSlotData => timeSlotData[date] !== null
        )
      );

      const weekData = {
        xLabel: xLabel,
        frequencyData: frequencyData,
      };

      this.weekGraphData.push(weekData);
      const graphOptions = this.setGraphOptions(weekData);

      setTimeout(() => {
        const chartDom = document.getElementById(
          `chart-container-interval-${this.behaviorId}-${0}`
        );

        const chartDomView = document.getElementById(
          `chart-container-interval-view-${this.behaviorId}-${0}`
        );

        if (chartDomView) {
          const chartInstanceView = echarts.init(chartDomView);
          chartInstanceView.setOption(graphOptions);
        }

        if (!chartDom) {
          console.error(
            `Chart container not found: chart-container-interval-${this.behaviorId}-${0}`
          );
          return;
        }

        this.initializeChart(chartDom, graphOptions, hasValidData);
      }, 0);
    } else {
      const chartOptions = this.setGraphOptions(freqDatasheetData);

      const datasheetDom = this.elementRef.nativeElement.querySelector(
        '#frequency-interval-datasheet'
      );
      if (datasheetDom) {
        const chartInstance = echarts.init(datasheetDom);
        this.chartInstance = chartInstance;
        chartInstance.setOption(chartOptions);
        chartInstance.on('finished', () => {
          this.emitChartAsImage();
        });
      }
    }
  }
  initializeChart(chartDom: any, graphOptions: any, hasValidData: boolean) {
    const chartInstance = echarts.init(chartDom);
    chartInstance.setOption(graphOptions);

    chartInstance.on('finished', () => this.chartRendered());

    setTimeout(() => {
      this.chartRendered();
    }, 2000);

    if (hasValidData) {
      this.chartInstances.push(chartInstance);
    }
  }

  chartRendered() {
    this.renderedChartsCount++;

    if (this.renderedChartsCount === this.totalCharts) {
      this.allChartsRendered = true;
      this.exportAllGraphs();
    }
  }

  setGraphOptions(freqDatasheetData: frequencyIntervalGraph) {
    const dates: string[] = freqDatasheetData.xLabel;
    const timeSlots: string[] = Object.keys(freqDatasheetData.frequencyData);

    const series = timeSlots.map(timeSlot => ({
      name: timeSlot,
      type: 'bar',
      data: dates.map(
        date => freqDatasheetData.frequencyData[timeSlot]?.[date] ?? ''
      ),
      label: { show: true },
      itemStyle: { borderRadius: [10, 10, 0, 0] },
    }));

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const datasheetGraphOption = {
      title: {
        text: 'Interval Frequency Graph',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '15%',
        bottom: '20%',
        containLabel: true,
      },
      legend: {
        bottom: '2%',
        left: '5%',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: { fontSize: 14, fontWeight: 400, color: '#475467' },
        data: timeSlots,
      },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: dates },
      yAxis: {
        type: 'value',
        name: 'Frequency',
        splitLine: { show: true },
        axisLine: { show: true },
        nameLocation: 'center',
        nameTextStyle: { padding: [0, 0, 8, 0] },
      },
      series,
      dataZoom: dataZoomConfig,
      graphic: {
        elements: [
          {
            type: 'rect',
            left: 0,
            top: 0,
            shape: { width: 0, height: 0 },
            draggable: true,
            ondrag: (dx: number) => {
              const chartContainer = document.getElementById('chart-container');
              if (!chartContainer) return;
              const chartInstance = echarts.getInstanceByDom(chartContainer);
              if (!chartInstance) return;

              const option = chartInstance.getOption() as any;
              const zoomFactor = (dx / chartContainer.offsetWidth) * 20;
              let newStart = option['dataZoom'][0].start + zoomFactor;
              let newEnd = newStart + 10;
              newStart = Math.max(0, Math.min(newStart, 90));
              newEnd = Math.min(100, newStart + 10);

              chartInstance.setOption(
                {
                  dataZoom: [{ start: newStart, end: newEnd }],
                  animationDurationUpdate: 300,
                },
                false
              );
            },
          },
        ],
      },
    };
    return datasheetGraphOption;
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });
    this.allChartImage.emit(images);
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }
}
